'use client';

import { ComponentProps, ReactNode } from 'react';
import { useCaptureSessionAttribution } from '@alltrails/session-storage';
import ReturningUserUpsell from '@alltrails/upsell/components/ReturningUserUpsell/ReturningUserUpsell';
import AppleRedirectUpsell from '@alltrails/user-auth/components/AppleRedirectUpsell/AppleRedirectUpsell';
import useAppleRedirectHandler from '@alltrails/user-auth/hooks/useAppleRedirectHandler';
import useBraze from '../hooks/useBraze';
import useOsano from '../hooks/useOsano';
import useTinuitiPixel from '../hooks/useTinuitiPixel';
import AmplitudeBrowserSdk from '../scripts/AmplitudeBrowserSdk';
import FooterWrapper from './FooterWrapper';
import GoogleOneTap from './GoogleOneTap';
import NavigationHeader from './NavigationHeader';

type PageProps = {
  children: ReactNode;
  disableInAppMessages?: boolean;
  doNotRenderMainContentId?: boolean;
  headerProps?: Omit<ComponentProps<typeof NavigationHeader>, 'algoliaConfigs'>;
  hideGoogleOneTap?: boolean;
  hideAppleRedirectUpsell?: boolean;
  hideReturningUserUpsell?: boolean;
  isFooterHidden?: boolean;
  logDwellTime?: boolean;
  seoHead?: ReactNode; // optional for app router, could be moved outside of Page
  pageViewLimitReached?: boolean;
  pageReloadOnLanguageChange?: boolean;
  navigation?: ReactNode;
};

const Page = ({
  children,
  disableInAppMessages,
  doNotRenderMainContentId,
  headerProps,
  hideGoogleOneTap = false,
  hideAppleRedirectUpsell = false,
  hideReturningUserUpsell = false,
  isFooterHidden = false,
  logDwellTime,
  seoHead,
  pageViewLimitReached,
  pageReloadOnLanguageChange,
  navigation
}: PageProps) => {
  useOsano();
  useTinuitiPixel();
  useBraze({ disableInAppMessages });
  useAppleRedirectHandler();
  useCaptureSessionAttribution();

  return (
    <>
      <AmplitudeBrowserSdk logDwellTime={logDwellTime} />
      {seoHead}
      {!hideGoogleOneTap && <GoogleOneTap pageViewLimitReached={pageViewLimitReached} />}
      {navigation ? navigation : <NavigationHeader {...headerProps} />}
      {doNotRenderMainContentId ? children : <main id="main-content">{children}</main>}
      {isFooterHidden ? null : <FooterWrapper pageReloadOnLanguageChange={pageReloadOnLanguageChange} />}
      {!hideReturningUserUpsell && <ReturningUserUpsell />}
      {!hideAppleRedirectUpsell && <AppleRedirectUpsell />}
    </>
  );
};

export default Page;
